import { Button, Stack, Tab, TableCell, TableRow, Tabs, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { config } from "../../../../config";
import { useApiServiceCallback } from "../../../../server/api/client";
import { EmailEngineSevice } from "../../../../server/api/email-engine-v2";
import { useSupabaseLoader } from "../../../../server/supabase/hooks";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { useEnabledChannels } from "../../../channels/hooks/use-enabled-channels";
import { PaginatedTable } from "../../../generic/components/paginated-table/paginated-table";
import { useAccount } from "../../../generic/hooks/use-account";
import { usePersonsInSegmentJson } from "../../../segment/components/manager-v2/moment-segment-preview/hooks/use-persons-in-segment-json";
import { SupabaseOptedOutService } from "../../server/supabase-opted-out-service";

import type { GetPersonsArgs } from "../../../segment/components/manager-v2/moment-segment-preview/hooks/use-persons-in-segment-json";
import type { GetPersonsInSegmentJsonPerson } from "../../../segment/types/segment-schema";
import type { OptedOut } from "../../server/supabase-opted-out-service";
import type { FC } from "react";

type Channels = "personal_email" | "slack" | "twilio" | "teams" | "whatsapp" | "work_email";

const tableFields: Record<Channels, { labels: string[]; values: (keyof OptedOut)[] }> = {
  personal_email: {
    labels: ["First Name", "Last Name", "Personal Email"],
    values: ["first_name", "last_name", "destination"],
  },
  slack: {
    labels: ["First Name", "Last Name", "Work Email"],
    values: ["first_name", "last_name", "work_email"],
  },
  twilio: {
    labels: ["First Name", "Last Name", "Phone Number", "Work Email"],
    values: ["first_name", "last_name", "destination", "work_email"],
  },
  teams: {
    labels: ["First Name", "Last Name", "Work Email"],
    values: ["first_name", "last_name", "work_email"],
  },
  whatsapp: {
    labels: ["First Name", "Last Name", "Phone Number", "Work Email"],
    values: ["first_name", "last_name", "destination", "work_email"],
  },
  work_email: {
    labels: ["First Name", "Last Name", "Work Email"],
    values: ["first_name", "last_name", "work_email"],
  },
};

// Everyone in the account, so we can list who has no destination for a channel
const ALL_PEOPLE: GetPersonsArgs = {
  segment_json: { type: "segment_group", operation: "AND", conditions: [] },
};

const missingText: Partial<Record<Channels, { heading: string; body?: string; empty: string }>> = {
  personal_email: {
    heading: "No Personal Email on record",
    empty: "We have a Personal Email for all of your employees.",
  },
  slack: {
    heading: "No Slack account found",
    body: "We can’t find a Slack account for these people. It could be that they don’t have a Slack account, or their email address may be incorrect.",
    empty: "We found a Slack account for all of your employees.",
  },
  twilio: {
    heading: "No phone number on record",
    empty: "We have a phone number for all of your employees.",
  },
  whatsapp: {
    heading: "No phone number on record",
    empty: "We have a phone number for all of your employees.",
  },
};

const constructChannelHeader = (
  channel: Channels,
  optedOutData: OptedOut[],
  onSyncSuppressionList?: () => Promise<void>,
  syncing?: boolean,
): React.ReactNode => {
  switch (channel) {
    case "personal_email":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: "center", justifyContent: "space-between" }}
          >
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <ChannelIcon channel="personal_email" />
              <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
                Personal Email
              </Typography>
            </Stack>
            <Button
              data-analytics-id="opt-outs-sync-personal-email"
              variant="outlined"
              size="small"
              loading={syncing}
              onClick={() => void onSyncSuppressionList?.().catch(console.error)}
            >
              Sync Unreachables
            </Button>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving Personal Email
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people may have blocked messages from this platform, or their email address
                may be incorrect.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of any issues with delivering to Personal Email.
            </Typography>
          )}
        </Stack>
      );
    case "slack":
      return (
        <Stack direction="row" sx={{ gap: 1 }}>
          <ChannelIcon channel="slack" />
          <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
            Slack
          </Typography>
        </Stack>
      );
    case "teams":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="teams" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              Teams
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving Teams Communications
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people may have blocked messages from your Teams bot, or their email address
                may be incorrect. Please check your HRIS, or ask your IT team.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of any issues with delivering Teams Communications.
            </Typography>
          )}
        </Stack>
      );
    case "twilio":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="twilio" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              SMS
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving SMS
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people may have replied “STOP” to a previous SMS message from you, or their
                provider has blocked messages from this platform, or their phone number may be
                invalid.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone who has opted out of SMS messages from you.
            </Typography>
          )}
        </Stack>
      );
    case "whatsapp":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="whatsapp" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              WhatsApp
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving WhatsApp
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people may have replied “STOP” to a previous WhatsApp message from you, their
                phone number may be incorrect, or they don’t have a WhatsApp account.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone who has opted out of WhatsApp messages from you.
            </Typography>
          )}
        </Stack>
      );
    case "work_email":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: "center", justifyContent: "space-between" }}
          >
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <ChannelIcon channel="work_email" />
              <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
                Work Email
              </Typography>
            </Stack>
            <Button
              data-analytics-id="opt-outs-sync-work-email"
              variant="outlined"
              size="small"
              loading={syncing}
              onClick={() => void onSyncSuppressionList?.().catch(console.error)}
            >
              Sync Unreachables
            </Button>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving Work Email
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people are not currently receiving Work Email Communications. They may have
                blocked emails from this platform, their email address may be incorrect, not set up
                for external emails, not fully set up when we first tried to contact them, or their
                inbox may be full. Please check your HRIS, or ask your IT team.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of any issues with delivering to Work Email.
            </Typography>
          )}
        </Stack>
      );
    default:
      return <></>;
  }
};

const constructTable = (channel: Channels, optedOutData: OptedOut[]): React.ReactNode => {
  const channelId = config.channel[channel];
  const { labels, values } = tableFields[channel];

  return (
    <PaginatedTable
      config={{
        headers: labels,
      }}
      data={optedOutData.filter((d) => d.channel_id === channelId)}
      renderRow={(optOut: OptedOut) => (
        <TableRow data-testid="opted-out-grid-row">
          {values.map((value) => (
            <TableCell key={value}>{optOut[value]}</TableCell>
          ))}
        </TableRow>
      )}
    />
  );
};

const constructMissing = (
  channel: Channels,
  missing: GetPersonsInSegmentJsonPerson[],
): React.ReactNode => {
  const text = missingText[channel];
  if (!text) return null;

  if (missing.length === 0) {
    return (
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        {text.empty}
      </Typography>
    );
  }

  return (
    <Stack sx={{ gap: 1 }}>
      <Stack>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
          {text.heading}
        </Typography>
        {text.body && (
          <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
            {text.body}
          </Typography>
        )}
      </Stack>
      <PaginatedTable
        config={{
          headers: ["First Name", "Last Name", "Work Email"],
        }}
        data={missing}
        renderRow={(person: GetPersonsInSegmentJsonPerson) => (
          <TableRow data-testid="missing-channel-grid-row">
            <TableCell>{person.first_name}</TableCell>
            <TableCell>{person.last_name}</TableCell>
            <TableCell>{person.email}</TableCell>
          </TableRow>
        )}
      />
    </Stack>
  );
};

const ALL_OPT_OUT_CHANNELS: { key: Channels; label: string }[] = [
  { key: "personal_email", label: "Personal Email" },
  { key: "slack", label: "Slack" },
  { key: "twilio", label: "SMS" },
  { key: "teams", label: "Teams" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "work_email", label: "Work Email" },
];

export const OptOuts: FC = () => {
  const { data: enabledChannels, loading: enabledChannelsLoading } = useEnabledChannels();
  const { data: account, loading: accountLoading } = useAccount();

  const { data: optedOutData, loading: optedOutLoading } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await new SupabaseOptedOutService(supabase).getAll(account_id, {
        order: [{ column: "last_name" }],
      });
      if (error) throw error;
      if (!data) throw new Error("No opted-out data found");
      return data;
    },
    [],
  );

  const availableChannels = useMemo(() => {
    if (!enabledChannels || !account) return [];
    return ALL_OPT_OUT_CHANNELS.filter(({ key }) => {
      if (key === "work_email") return account.enable_work_email;
      if (key === "personal_email") return account.enable_personal_email;
      return enabledChannels.includes(key);
    });
  }, [enabledChannels, account]);

  const [displayedTab, setDisplayedTab] = useState<Channels>("twilio");
  const [syncing, setSyncing] = useState(false);

  const handleSyncSuppressionList = useApiServiceCallback(
    EmailEngineSevice,
    async ({ apiService }) => {
      setSyncing(true);
      try {
        const { error } = await apiService.updateSuppressionList();
        if (error) {
          toast.error("Failed to request opt-out sync. Please try again.");
        } else {
          toast.success("Sync requested. Please check back in a few minutes for updated records.");
        }
      } catch {
        toast.error("Failed to request opt-out sync. Please try again.");
      } finally {
        setSyncing(false);
      }
    },
    [],
  );

  // Fall back to the first available channel if the selected tab is not accessible
  const activeTab = useMemo(() => {
    const keys = availableChannels.map((c) => c.key);
    return keys.includes(displayedTab) ? displayedTab : keys[0];
  }, [availableChannels, displayedTab]);

  const channelSpecificData = useMemo(() => {
    if (optedOutLoading || !activeTab) return [];
    if (!optedOutData) return [];

    return optedOutData.filter((item) => item.channel_id === config.channel[activeTab]);
  }, [optedOutLoading, optedOutData, activeTab]);

  const {
    data: people,
    loading: peopleLoading,
    error: peopleError,
  } = usePersonsInSegmentJson(ALL_PEOPLE);

  // People with no destination on record for each channel that has a "no X" table
  const missingByChannel = useMemo(
    () =>
      Object.fromEntries(
        availableChannels
          .filter(({ key }) => missingText[key])
          .map(({ key }) => [key, (people ?? []).filter((person) => !person.channels?.[key])]),
      ) as Partial<Record<Channels, GetPersonsInSegmentJsonPerson[]>>,
    [availableChannels, people],
  );

  const tabCount = (channel: Channels): number =>
    (optedOutData ?? []).filter((item) => item.channel_id === config.channel[channel]).length +
    (missingByChannel[channel]?.length ?? 0);

  if (enabledChannelsLoading || accountLoading || !activeTab) return null;

  return (
    <Stack sx={{ gap: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
        Unreachable People
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        These people will not receive Communications through specific platforms. Some don’t have
        this platform set up on their account. Others may have blocked or opted out of the platform,
        or their contact details may be incorrect.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        SMS and WhatsApp opt-outs are recorded immediately. Email and Teams blocks or invalid
        details are only detected when you next send that person a Communication and their email
        provider or Microsoft Teams notifies us that delivery was blocked.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        If their details are incorrect or invalid, you’ll need to correct them in their employee
        record.
      </Typography>
      {/* oxlint-disable-next-line typescript/no-unsafe-type-assertion */}
      <Tabs value={activeTab} onChange={(e, tab) => setDisplayedTab(tab as Channels)}>
        {availableChannels.map(({ key, label }) => (
          <Tab
            key={key}
            label={
              optedOutLoading || peopleLoading || peopleError
                ? label
                : `${label} (${tabCount(key)})`
            }
            value={key}
          />
        ))}
      </Tabs>
      {constructChannelHeader(activeTab, channelSpecificData, handleSyncSuppressionList, syncing)}
      {channelSpecificData.length > 0 && constructTable(activeTab, channelSpecificData)}
      {!peopleLoading &&
        !peopleError &&
        constructMissing(activeTab, missingByChannel[activeTab] ?? [])}
    </Stack>
  );
};
